/**
 * Helpers for Parent Information ↔ existing reenrolment write APIs.
 * AIC Connect read payloads are used as-is in the UI; these only shape
 * requests that the reenrolment backend still expects in the legacy format.
 */

/** Given name from AIC Connect (`student_name`) or older TASS keys. */
export const getStudentGivenName = (student) =>
    String(student?.student_name || student?.given_name || student?.first_name || '').trim();

/** Surname from AIC Connect (`student_surname`) or older TASS keys. */
export const getStudentSurname = (student) =>
    String(student?.student_surname || student?.surname || '').trim();

/** Display name: student_name + student_surname (legacy first/given + surname still work). */
export const getStudentFullName = (student) => {
    const given = getStudentGivenName(student);
    const surname = getStudentSurname(student);
    if (!surname) return given;
    if (!given) return surname;
    if (given.toLowerCase().endsWith(surname.toLowerCase())) return given;
    return `${given} ${surname}`;
};

/** Flat student rows for the medical form (API fields + campus meta). */
export const flattenParentStudents = (parent, { skipNegativeWalletCampuses = false } = {}) => {
    if (!parent?.parent_codes) return [];

    const familySurname = (parent.surname || '').trim();

    return parent.parent_codes.flatMap((campus) => {
        const wallet_balance = toNumberAmount(campus.wallet_balance);
        // Outstanding page: hide campuses that are paid up (0) or in credit (< 0).
        if (skipNegativeWalletCampuses && wallet_balance != null && wallet_balance <= 0) {
            return [];
        }

        return (campus.students || []).map((student) => {
            const student_surname = getStudentSurname(student) || familySurname;
            return {
                ...student,
                parent_code: campus.parent_code,
                campus_code: campus.campus_code,
                campus_name: campus.campus_name,
                ...(student.student_medical_data || {}),
                student_surname,
                surname: student_surname,
                wallet_balance,
            };
        });
    });
};

export const CAMPUS_CODE_TO_NAME = {
    '01': 'Kewdale',
    '02': 'Thornlie',
    '03': 'Dianella',
    '04': 'Adelaide',
    '05': 'Forrestdale',
    '06': 'Henley Brook',
};

export const getChildCampusName = (child) =>
    (child?.campus_name || '').trim() ||
    CAMPUS_CODE_TO_NAME[String(child?.campus_code || child?.cmpy_code || '').trim()] ||
    '';

/**
 * A student re-enrols into next year's grade, so anyone already in Year 12
 * (or beyond) has no year to move into.
 */
export const isEligibleForReenrolment = (child) => {
    const current = Number(child?.year_grp ?? child?.year);
    if (!Number.isFinite(current)) return true;
    return current + 1 <= 12;
};

/** Year 13+ are not re-enrolled, so they do not count toward sibling discount. */
export const isFeeEligibleChild = (child) => {
    if (child == null) return false;
    const year = child.year_grp ?? child.year;
    if (year === null || year === undefined || year === '') return true;
    return Number(year) < 13;
};

/**
 * Family-wide eligible children for sibling / family discount.
 * 2 Kewdale + 1 Thornlie counts as 3, not as 2 and 1 per campus.
 */
export const countFeeEligibleChildren = (children) =>
    (Array.isArray(children) ? children : []).filter(isFeeEligibleChild).length;

export const toNumberAmount = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
};

/** Positive TASS wallet only. Zero and credit are not outstanding. */
export const isPositiveOutstandingAmount = (value) => {
    const amount = toNumberAmount(value);
    return amount != null && amount > 0;
};

/** Login Outstanding=Yes is ignored when the amount is zero or a credit. */
export const isLoginOutstanding = (loginData) => {
    if (loginData?.Outstanding !== 'Yes') return false;
    const amount = toNumberAmount(loginData.OutstandingAmount);
    if (amount != null && amount <= 0) return false;
    return true;
};

/** Negative AIC Connect wallet_balance (TASS bal_fwd) is a campus credit. */
export const walletBalanceToCredit = (walletBalance) => {
    const bal = toNumberAmount(walletBalance);
    if (bal == null || bal >= 0) return 0;
    return Math.abs(bal);
};

/** Positive AIC Connect wallet_balance is what the campus is still owed for the current year. */
export const walletBalanceToOutstanding = (walletBalance) => {
    const bal = toNumberAmount(walletBalance);
    if (bal == null || bal <= 0) return 0;
    return bal;
};

/** Per-campus credits keyed by parent_code and campus name — for /insertdatalog. */
export const buildCampusCredits = (parent) => {
    const map = {};
    (parent?.parent_codes || []).forEach((campus) => {
        const credit = walletBalanceToCredit(campus.wallet_balance);
        const parentCode = String(campus.parent_code || '').trim();
        const campusName = String(campus.campus_name || '').trim();
        if (parentCode) map[parentCode] = credit;
        if (campusName) map[campusName] = credit;
    });
    return map;
};

/**
 * Per-campus current-year debt, the same shape and the same keys as the credits map.
 *
 * One wallet gives both maps, so a campus appears in exactly one of them with a figure and in the
 * other with nought — a campus cannot owe and be owed at the same time. Keeping them apart is what
 * lets the payment page show a debt as its own line instead of as a fee that has grown.
 */
export const buildCampusOutstanding = (parent) => {
    const map = {};
    (parent?.parent_codes || []).forEach((campus) => {
        const owed = walletBalanceToOutstanding(campus.wallet_balance);
        const parentCode = String(campus.parent_code || '').trim();
        const campusName = String(campus.campus_name || '').trim();
        if (parentCode) map[parentCode] = owed;
        if (campusName) map[campusName] = owed;
    });
    return map;
};

/** Campuses / parent codes that still owe (positive wallet only). */
export const getCampusOutstandingRows = (parent) => {
    const rows = (parent?.parent_codes || [])
        .map((campus) => ({
            parent_code: campus.parent_code,
            campus_name: campus.campus_name,
            amount: toNumberAmount(campus.wallet_balance) ?? 0,
        }))
        .filter((row) => row.amount > 0);

    if (rows.length > 0) return rows;

    const top = toNumberAmount(parent?.wallet_balance);
    if (top != null && top > 0) {
        return [{
            parent_code: parent?.parent_code,
            campus_name: parent?.campus_name,
            amount: top,
        }];
    }

    return [];
};

/** Amount owing: positive campus wallets only (negative is credit, ignored). */
export const getParentWalletBalance = (parent) =>
    getCampusOutstandingRows(parent).reduce((sum, row) => sum + row.amount, 0);

/** True when any linked parent code still has a positive TASS wallet. */
export const hasAnyParentCodeOutstanding = (parent) =>
    getCampusOutstandingRows(parent).length > 0;

export const groupChildrenByCampus = (children = []) => {
    const groups = [];
    const byCampus = new Map();

    children.forEach((child) => {
        const campus = getChildCampusName(child) || 'Unknown campus';
        if (!byCampus.has(campus)) {
            const group = { campus, children: [] };
            byCampus.set(campus, group);
            groups.push(group);
        }
        byCampus.get(campus).children.push(child);
    });

    return groups;
};

const CAMPUS_PORTAL_CODE = {
    Kewdale: 'k',
    Thornlie: 't',
    Dianella: 'd',
    Adelaide: 'a',
    Forrestdale: 'f',
    'Henley Brook': 'h',
};

export const getCampusParentPortalUrl = (campusName) =>
    `https://portal-${CAMPUS_PORTAL_CODE[campusName] || ''}.aic.wa.edu.au/parentlounge/index.cfm?do=parentportal.accounts.main.accounts.payment`;

export const getPrimaryCampusName = (parent, loggedInParentCode) => {
    const codes = parent?.parent_codes || [];
    const match = codes.find(
        (c) => String(c.parent_code).trim() === String(loggedInParentCode || '').trim()
    );
    return match?.campus_name || codes[0]?.campus_name || '';
};

const studentCodeOf = (child) =>
    String(child?.stud_code || child?.student_code || '').trim();

/** Attach campus meta from parent-history students onto enrolled rows. */
export const enrichEnrolledChildrenWithCampus = (enrolled, allStudents = []) => {
    const byCode = new Map(
        (allStudents || []).map((s) => [studentCodeOf(s), s])
    );

    return (enrolled || []).map((child) => {
        const local = byCode.get(studentCodeOf(child));
        const campus_code = local?.campus_code || child.campus_code || child.cmpy_code || '';
        return {
            ...child,
            campus_code,
            campus_name: getChildCampusName({ ...child, ...local, campus_code }),
            parent_code: local?.parent_code || child.parent_code,
            imageUrl: child.imageUrl || local?.imageUrl || '',
            student_name: child.student_name || local?.student_name || child.first_name,
            student_surname: child.student_surname || local?.student_surname || child.surname,
            surname: child.surname || local?.student_surname || local?.surname,
        };
    });
};

/**
 * Enrolled children for Re-enrolment / fees, from Parent Information form state.
 * year_grp is next year (current + 1), matching the previous TASS response mapping.
 */
export const buildEnrolledChildrenFromForm = (allStudents, medicalRecords, familySurname = '') => {
    return (allStudents || []).flatMap((student) => {
        const code = studentCodeOf(student);
        if (!code) return [];

        const medical = (medicalRecords || []).find(
            (r) => studentCodeOf(r) === code
        );
        if (!medical || medical.enrollStatus !== 'Enrolled') return [];

        const surname = getStudentSurname(student) || familySurname;
        return [{
            stud_code: code,
            first_name: getStudentGivenName(student),
            student_name: getStudentGivenName(student),
            student_surname: surname,
            surname,
            year_grp: Number(student.year_grp ?? student.year) + 1,
            cmpy_code: student.campus_code || student.cmpy_code,
            campus_code: student.campus_code || student.cmpy_code,
            campus_name: getChildCampusName(student),
            parent_code: student.parent_code,
            imageUrl: student.imageUrl || medical.imageUrl || '',
        }];
    });
};

export const getParentEmails = (parent) => {
    const email1 = parent?.contact?.email1?.trim() || '';
    const email2 = parent?.contact?.email2?.trim() || '';
    return {
        email1,
        email2,
        combined: [email1, email2].filter(Boolean).join(','),
    };
};

/** Shape expected by /insertdatalog and Reenrolment navigation. */
export const toReenrolmentChildren = (students, familySurname = '') =>
    (students || []).map((s) => {
        const surname = getStudentSurname(s) || familySurname;
        return {
            stud_code: s.student_code,
            first_name: getStudentGivenName(s),
            student_name: getStudentGivenName(s),
            student_surname: surname,
            surname,
            year_grp: Number(s.year),
            cmpy_code: s.campus_code,
            parent_code: s.parent_code,
            campus_name: s.campus_name,
            section: s.section,
            imageUrl: s.imageUrl || '',
        };
    });

/** Shape expected by /savePersonalandMedDetails and /updatePersonalandMedDetails. */
export const toReenrolmentMedicalRecords = (records, familySurname = '') =>
    (records || []).map((r) => {
        const {
            student_code,
            campus_code,
            parent_code,
            campus_name,
            private_health_insurance,
            student_medical_data,
            imageUrl,
            ...rest
        } = r;

        return {
            ...rest,
            stud_code: student_code,
            given_name: getStudentGivenName(r),
            student_name: getStudentGivenName(r),
            student_surname: getStudentSurname(r) || familySurname,
            surname: getStudentSurname(r) || familySurname,
            cmpy_code: campus_code,
            parent_code,
            campus_name,
            private_healthinsurance: private_health_insurance ?? rest.private_healthinsurance,
            imageUrl: imageUrl || '',
        };
    });
